import BackButton from 'components/BackButton';
import PageTitle from 'components/PageTitle';
import RoundedButton from 'components/RoundedButton';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PartnerDiscountCard from 'components/subscriber-perks/PartnerDiscountCard';
import PartnerDiscountModal from 'components/subscriber-perks/PartnerDiscountModal';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { ReactNode, useState } from 'react';
import { ASSET_PREFIX } from 'utils/constants';

interface IPartnerDiscount {
  partnerCode: string;
  heroImage: string;
  iconComponent: ReactNode;
  discountComponent: ReactNode;
  conditionComponent: string;
  conditionTextCardOverride?: string;
}

export const PartnerDiscounts: IPartnerDiscount[] = [
  {
    partnerCode: 'VED',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/ved-main.jpg`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/ved.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>15%</strong> OFF
      </>
    ),
    conditionComponent: 'Site-wide',
  },
  {
    partnerCode: 'URBAN-B',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/urban-b-main.jpg`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/urban-b.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>10%</strong> OFF
      </>
    ),
    conditionComponent: 'Site-wide (urban B products only)',
  },
  {
    partnerCode: 'COCOA-CABANA',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/cocoa-cabana-main.jpg`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/cocoa-cabana.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>10%</strong> OFF
      </>
    ),
    conditionComponent: 'Site-wide',
  },
  {
    partnerCode: 'WILD-Deodorant',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/wild.jpg`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/wild.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>20%</strong> OFF
      </>
    ),
    conditionComponent: 'Site-wide',
  },
  {
    partnerCode: 'HELLOFRESH',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/hellofresh-main.jpeg`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/hellofresh.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>55%</strong> OFF
      </>
    ),
    conditionComponent:
      'Order your first box with 55% off and free treats for 3 months!',
  },
  {
    partnerCode: 'CLARITY-BLEND',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/clarity-main.webp`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/clarity.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>20%</strong> OFF
      </>
    ),
    conditionComponent: 'Site-wide',
  },

  {
    partnerCode: 'BEEHEMIAN-WRAPZODY',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/beehemian-wrapzody-main.jpg`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/beehemian-wrapzody.jpg`}
      />
    ),
    discountComponent: (
      <>
        <strong>10%</strong> OFF
      </>
    ),
    conditionComponent: '10% off your order',
  },
  {
    partnerCode: 'GREEN-CHEF',
    heroImage: `${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/green-chef-main.webp`,
    iconComponent: (
      <img
        src={`${ASSET_PREFIX}/images/subscriber-perks/partner-discounts/green-chef.png`}
      />
    ),
    discountComponent: (
      <>
        <strong>50%</strong> OFF
      </>
    ),
    conditionComponent:
      '50% off the first box (plus free shipping) and 25% of the next 4',
  },
];

const MyDiscounts = observer(() => {
  const { navigateToAccount } = useUIStore();
  const { subscriptionStatus } = useUserStore();
  const userIsEligibleForSubscriberDiscounts = ['PREMIUM', 'LITE'].includes(
    subscriptionStatus,
  );

  const [partnerDiscountModalToDisplay, setPartnerDiscountModalToDisplay] =
    useState<IPartnerDiscount>();

  return (
    <MainLayout>
      <NextSeo
        title="Competition Subscriber Perks & Discounts | DAYMADE"
        description="Enter competitions online with DAYMADE and get access to an array of subscriber perks and discounts. Includes savings, points, and even some environmental benefits!"
        openGraph={{
          title: 'Competition Subscriber Perks & Discounts | DAYMADE',
          description:
            'Enter competitions online with DAYMADE and get access to an array of subscriber perks and discounts. Includes savings, points, and even some environmental benefits!',
        }}
      />

      <PageTitle>
        My <strong>Discounts</strong>
      </PageTitle>

      <PaddedMaxWidthContainer style={{ padding: '2em' }}>
        <p style={{ fontSize: '1.3em', textAlign: 'center' }}>
          {userIsEligibleForSubscriberDiscounts ? (
            <>
              As a DAYMADE subscriber, you get access to all of these discounts!
            </>
          ) : (
            <>
              <Link href="/account/subscriptions/upgrade">
                <a style={{ textDecoration: 'underline' }}>Upgrade</a>
              </Link>{' '}
              to a Lite or Premium subscription to unlock.
            </>
          )}
        </p>
      </PaddedMaxWidthContainer>

      {partnerDiscountModalToDisplay && (
        <PartnerDiscountModal
          partnerDiscount={partnerDiscountModalToDisplay}
          handleDismiss={() => setPartnerDiscountModalToDisplay(undefined)}
        />
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          maxWidth: 900,
          margin: '0 2em 3em',
          width: '95%',
        }}
      >
        {PartnerDiscounts.map((partnerDiscount) => (
          <PartnerDiscountCard
            key={partnerDiscount.partnerCode}
            partnerDiscount={partnerDiscount}
            showAsLocked={userIsEligibleForSubscriberDiscounts === false}
            onClick={() => setPartnerDiscountModalToDisplay(partnerDiscount)}
          />
        ))}
      </div>

      {userIsEligibleForSubscriberDiscounts === false && (
        <RoundedButton
          href="/account/subscriptions/upgrade"
          style={{ minWidth: 300 }}
        >
          Upgrade Now
        </RoundedButton>
      )}

      <BackButton
        onClick={() => navigateToAccount()}
        style={{ marginBottom: '4em' }}
      >
        Back
      </BackButton>
    </MainLayout>
  );
});

export default MyDiscounts;
